import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  XMarkIcon,
  EyeIcon,
  EyeSlashIcon,
  KeyIcon,
  SparklesIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { registrationService } from '../services/registrationService';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function ClientLoginDrawer({ isOpen, onClose }: Props) {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [forgot, setForgot] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMsg, setResetMsg] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setEmail(''); setPassword(''); setError(''); setShowPw(false);
      setForgot(false); setResetEmail(''); setResetMsg('');
    }
  }, [isOpen]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const response = await registrationService.loginClient(email, password);
      if (response.success) {
        if (response.token) {
          localStorage.setItem('authToken', response.token);
          localStorage.setItem('userType', 'client');
          localStorage.setItem('userData', JSON.stringify({
            ...response.user,
            must_verify: response.mustVerify,
            must_reset_password: response.mustResetPassword,
          }));
        }
        onClose();
        if (response.mustVerify) { navigate('/verify', { replace: true }); return; }
        if (response.mustResetPassword) { navigate('/fill-profile', { replace: true }); return; }
        navigate('/dashboard/client', { replace: true });
      } else {
        setError(response.message || 'Login failed');
      }
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetLoading(true); setResetMsg('');
    try {
      const result = await registrationService.requestPasswordReset(resetEmail);
      setResetMsg(result.message || 'Reset link sent.');
    } catch {
      setResetMsg('Something went wrong. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />

          <motion.div
            key="drawer"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed top-0 right-0 h-full w-full max-w-sm z-50 flex flex-col bg-gradient-to-b from-indigo-950 via-violet-950 to-violet-900 shadow-2xl overflow-y-auto"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-gradient-to-br from-violet-400 to-indigo-500 rounded-lg flex items-center justify-center">
                  <SparklesIcon className="w-4 h-4 text-white" />
                </div>
                <span className="text-white font-semibold text-sm">CoachSight ECR</span>
              </div>
              <button onClick={onClose} className="text-violet-400 hover:text-white transition-colors">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 px-6 py-8">
              <AnimatePresence mode="wait">
                <motion.div
                  key={forgot ? 'forgot' : 'login'}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.18 }}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-9 h-9 bg-violet-500/20 rounded-xl flex items-center justify-center">
                      <UserIcon className="w-5 h-5 text-violet-400" />
                    </div>
                    <h2 className="text-xl font-bold text-white">
                      {forgot ? 'Reset password' : 'Client portal'}
                    </h2>
                  </div>

                  {forgot ? (
                    <form onSubmit={handleForgot} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-200 mb-1">Email address</label>
                        <input
                          type="email"
                          value={resetEmail}
                          onChange={e => setResetEmail(e.target.value)}
                          className="w-full px-3 py-2.5 border border-white/10 bg-white/5 text-gray-200 placeholder-violet-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
                          placeholder="your@email.com"
                          required
                          autoFocus
                        />
                      </div>
                      {resetMsg && (
                        <div className="p-3 bg-blue-900/30 border border-blue-800 rounded-xl">
                          <p className="text-blue-300 text-sm">{resetMsg}</p>
                        </div>
                      )}
                      <button
                        type="submit"
                        disabled={resetLoading}
                        className={`w-full flex items-center justify-center py-2.5 rounded-xl font-semibold text-sm ${resetLoading ? 'bg-gray-600 cursor-not-allowed text-gray-400' : 'bg-violet-600 hover:bg-violet-500 text-white'}`}
                      >
                        {resetLoading ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Sending…</> : 'Send Reset Link'}
                      </button>
                      <button type="button" onClick={() => { setForgot(false); setResetMsg(''); }} className="w-full text-sm text-violet-400 hover:text-violet-300 transition-colors">
                        Back to login
                      </button>
                    </form>
                  ) : (
                    <form onSubmit={handleLogin} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-200 mb-1">Email</label>
                        <input
                          type="email"
                          value={email}
                          onChange={e => setEmail(e.target.value)}
                          className="w-full px-3 py-2.5 border border-white/10 bg-white/5 text-gray-200 placeholder-violet-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
                          placeholder="your@email.com"
                          required
                          autoFocus
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-200 mb-1">Password</label>
                        <div className="relative">
                          <input
                            type={showPw ? 'text' : 'password'}
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            className="w-full px-3 py-2.5 pr-10 border border-white/10 bg-white/5 text-gray-200 placeholder-violet-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
                            placeholder="Enter your password"
                            required
                          />
                          <button type="button" onClick={() => setShowPw(!showPw)} className="absolute inset-y-0 right-0 pr-3 flex items-center">
                            {showPw ? <EyeSlashIcon className="h-4 w-4 text-violet-500" /> : <EyeIcon className="h-4 w-4 text-violet-500" />}
                          </button>
                        </div>
                      </div>
                      {error && (
                        <div className="p-3 bg-red-900/30 border border-red-800 rounded-xl">
                          <p className="text-red-300 text-sm">{error}</p>
                        </div>
                      )}
                      <button
                        type="submit"
                        disabled={loading}
                        className={`w-full flex items-center justify-center py-2.5 rounded-xl font-semibold text-sm ${loading ? 'bg-gray-600 cursor-not-allowed text-gray-400' : 'bg-violet-600 hover:bg-violet-500 text-white'}`}
                      >
                        {loading ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Signing in…</> : <><KeyIcon className="w-4 h-4 mr-2" />Sign in</>}
                      </button>
                      <button type="button" onClick={() => setForgot(true)} className="w-full text-xs text-violet-400 hover:text-violet-300 transition-colors text-left">
                        Forgot password?
                      </button>
                    </form>
                  )}

                  <div className="mt-8 p-4 bg-white/5 border border-white/10 rounded-xl">
                    <p className="text-violet-400 text-xs text-center italic">Your coach creates your account — you can't self-register here.</p>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
